/**
 * <kr-collection> and <kr-filter-drawer> — filtering, sorting and pagination for the custom
 * collection section.
 *
 * Deliberately independent of Horizon's `facets.js` / `results-list.js`: those are built on the
 * theme's `Component` base class and expect its `ref=` markup, and `PaginatedList` hard-requires
 * a `[ref="cardGallery"]` element that `kr-product-card` does not emit. This file talks to the
 * Section Rendering API directly and touches nothing outside its own section.
 *
 * Everything degrades. Without JavaScript the filter panel is a plain always-visible form with a
 * submit button, the sort select submits that same form, and pagination is ordinary links; the
 * script only intercepts them to avoid full page loads.
 *
 * Markup contract inside <kr-collection>:
 *   [data-kr-filter-form]  — the form whose FormData becomes the query string
 *   [data-kr-results]      — the region swapped on every update
 *   [data-kr-grid]         — the <ul>; carries data-last-page / data-current-page
 *   [data-kr-chips]        — active filter chips
 *   [data-kr-count]        — the result count
 *   [data-kr-filters]      — the <kr-filter-drawer>
 *   [data-kr-more]         — load-more wrapper, containing [data-kr-load-more]
 *   [data-kr-announce]     — aria-live region
 *   [data-kr-sort]         — the sort <select>
 *   [data-kr-density]      — a density button, value "compact" | "comfortable"
 *   [data-kr-nojs]         — removed on connect
 */

const PRICE_DEBOUNCE_MS = 400;
const DENSITY_KEY = 'kr-collection-density';
const DESKTOP_QUERY = '(min-width: 990px)';
const MAX_RESTORE_PAGES = 10;

class KrCollection extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {AbortController | undefined} */
  #fetchController;
  /** @type {IntersectionObserver | undefined} */
  #scrollObserver;
  /** @type {number | undefined} */
  #debounceId;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    for (const el of this.querySelectorAll('[data-kr-nojs]')) el.remove();

    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('input', this.#handleInput, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('submit', this.#handleSubmit, { signal });
    window.addEventListener('popstate', this.#handlePopState, { signal });

    this.#restoreDensity();
    this.#restorePages().finally(() => this.#setUpInfiniteScroll());
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#fetchController?.abort();
    this.#scrollObserver?.disconnect();
    if (this.#debounceId) clearTimeout(this.#debounceId);
  }

  get #sectionId() {
    return this.dataset.sectionId ?? '';
  }

  get #form() {
    return /** @type {HTMLFormElement | null} */ (this.querySelector('[data-kr-filter-form]'));
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.matches('[data-kr-sort]')) {
      this.#applyForm();
      return;
    }

    // Price inputs fire `input`, not `change`, for keystrokes; `change` covers blur/steppers.
    if (target.matches('[data-kr-price-input]')) {
      this.#applyForm();
      return;
    }

    if (target.closest('[data-kr-filter-form]') && target.matches('input[type="checkbox"]')) {
      this.#applyForm();
    }
  };

  /** @param {Event} event */
  #handleInput = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.matches('[data-kr-price-input]')) return;

    if (this.#debounceId) clearTimeout(this.#debounceId);
    this.#debounceId = setTimeout(() => this.#applyForm(), PRICE_DEBOUNCE_MS);
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target.closest) return;

    const density = target.closest('[data-kr-density]');
    if (density instanceof HTMLElement) {
      this.#setDensity(density.dataset.krDensity ?? 'compact');
      return;
    }

    const showMore = target.closest('[data-kr-filter-more]');
    if (showMore instanceof HTMLElement) {
      this.#toggleFilterOverflow(showMore);
      return;
    }

    const loadMore = target.closest('[data-kr-load-more]');
    if (loadMore instanceof HTMLAnchorElement) {
      event.preventDefault();
      this.#appendPage(loadMore.href);
      return;
    }

    // Chips, "clear all", the filter panel's clear link and numbered pages are all plain
    // anchors; intercepting them keeps the interaction on the same page.
    const link = target.closest(
      '[data-kr-chip], .kr-collection__page, .kr-collection__filters-clear'
    );
    if (link instanceof HTMLAnchorElement && !event.metaKey && !event.ctrlKey) {
      event.preventDefault();
      this.#render(new URL(link.href), { scroll: link.classList.contains('kr-collection__page') });
    }
  };

  /** @param {SubmitEvent} event */
  #handleSubmit = (event) => {
    const form = /** @type {HTMLElement} */ (event.target);
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-kr-filter-form]')) return;

    event.preventDefault();
    this.#applyForm();
    this.#drawer?.close();
  };

  #handlePopState = () => {
    this.#render(new URL(window.location.href), { push: false });
  };

  get #drawer() {
    return /** @type {KrFilterDrawer | null} */ (this.querySelector('[data-kr-filters]'));
  }

  /** Builds the query string from the filter form and re-renders. */
  #applyForm() {
    if (this.#debounceId) clearTimeout(this.#debounceId);

    const form = this.#form;
    const url = new URL(this.dataset.resultsUrl || window.location.pathname, window.location.origin);

    if (form) {
      const params = new URLSearchParams(/** @type {any} */ (new FormData(form)));

      // Empty price bounds would otherwise filter to "0 and up" / "up to 0".
      for (const key of [...params.keys()]) {
        if (params.get(key) === '') params.delete(key);
      }
      params.delete('page');

      url.search = params.toString();
    }

    this.#render(url);
  }

  /**
   * @param {URL} url
   * @param {{ push?: boolean, scroll?: boolean }} [options]
   */
  async #render(url, { push = true, scroll = false } = {}) {
    const results = this.querySelector('[data-kr-results]');
    if (!(results instanceof HTMLElement)) return;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    results.setAttribute('aria-busy', 'true');

    try {
      const doc = await this.#fetchSection(url, this.#fetchController.signal);
      if (!doc) return;

      if (push) history.pushState({ krCollection: true }, '', url.toString());

      // Swapping four targeted nodes rather than morphing the whole section keeps the
      // drawer open, the scroll position intact and the DOM churn small.
      this.#swap(doc, '[data-kr-results]');
      this.#swap(doc, '[data-kr-chips]');
      this.#swap(doc, '[data-kr-count]');
      this.#swapFilters(doc);
      this.#syncFilterTriggerCount(doc);

      this.#announce();
      this.#setUpInfiniteScroll();

      if (scroll) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }
    } catch (error) {
      if (/** @type {Error} */ (error)?.name === 'AbortError') return;
      // A failed fetch should not strand the visitor on a stale page.
      window.location.assign(url.toString());
    } finally {
      results.setAttribute('aria-busy', 'false');
    }
  }

  /**
   * @param {URL} url
   * @param {AbortSignal} [signal]
   * @returns {Promise<Document | null>}
   */
  async #fetchSection(url, signal) {
    const sectionUrl = new URL(url.toString());
    sectionUrl.searchParams.set('section_id', this.#sectionId);

    const response = await fetch(sectionUrl.toString(), { signal });
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  /**
   * @param {Document} doc
   * @param {string} selector
   */
  #swap(doc, selector) {
    const next = doc.querySelector(selector);
    const current = this.querySelector(selector);

    if (!current) return;

    if (!next) {
      // The node is conditional (chips disappear when nothing is active) — empty it rather
      // than removing it, so the next update still has somewhere to write.
      current.replaceChildren();
      return;
    }

    current.replaceChildren(...next.childNodes);
  }

  /**
   * Filters are re-rendered so counts and disabled states stay accurate, but the open/closed
   * state of each panel is the visitor's, not the server's — so it is carried across.
   *
   * @param {Document} doc
   */
  #swapFilters(doc) {
    const next = doc.querySelector('[data-kr-filters]');
    const current = this.querySelector('[data-kr-filters]');
    if (!next || !current) return;

    const openState = [...current.querySelectorAll('details.kr-filter')].map((el) => el.open);

    current.replaceChildren(...next.childNodes);

    const panels = current.querySelectorAll('details.kr-filter');
    panels.forEach((panel, index) => {
      if (openState[index] !== undefined) panel.open = openState[index];
    });
  }

  /** @param {Document} doc */
  #syncFilterTriggerCount(doc) {
    const trigger = this.querySelector('[data-kr-filter-open]');
    const nextTrigger = doc.querySelector('[data-kr-filter-open]');
    if (!(trigger instanceof HTMLElement) || !(nextTrigger instanceof HTMLElement)) return;

    const expanded = trigger.getAttribute('aria-expanded');
    trigger.replaceChildren(...nextTrigger.childNodes);
    if (expanded) trigger.setAttribute('aria-expanded', expanded);
  }

  #announce() {
    const announce = this.querySelector('[data-kr-announce]');
    const count = this.querySelector('[data-kr-count]');
    if (announce instanceof HTMLElement) {
      announce.textContent = count?.textContent?.trim() ?? '';
    }
  }

  /**
   * Load more keeps the address on page 1 and remembers how many pages are showing in
   * `history.state`. A refresh (or coming back from a product) therefore always starts with
   * the first page and then re-appends the rest, instead of landing on `?page=N` with the
   * first products missing.
   */
  async #restorePages() {
    if (this.dataset.pagination === 'numbered') return;

    const grid = this.querySelector('[data-kr-grid]');
    if (!(grid instanceof HTMLElement)) return;

    // An address that still carries `?page=N` (older visit, shared link): put the earlier
    // pages back in front and clean the address.
    const url = new URL(window.location.href);
    const urlPage = Math.min(Number(url.searchParams.get('page')) || 1, MAX_RESTORE_PAGES);

    if (urlPage > 1) {
      url.searchParams.delete('page');

      try {
        const requests = [];
        for (let page = 1; page < urlPage; page += 1) {
          const pageUrl = new URL(url.toString());
          if (page > 1) pageUrl.searchParams.set('page', String(page));
          requests.push(this.#fetchSection(pageUrl));
        }

        const docs = await Promise.all(requests);
        const items = docs.flatMap((doc) => [...(doc?.querySelectorAll('[data-kr-grid] > li') ?? [])]);
        grid.prepend(...items);

        history.replaceState({ krCollection: true, krPages: urlPage }, '', url.toString());
      } catch {
        // Leave the page as the server rendered it.
      }
      return;
    }

    const target = Math.min(Number(history.state?.krPages) || 1, MAX_RESTORE_PAGES);

    while ((Number(grid.dataset.currentPage) || 1) < target) {
      const before = Number(grid.dataset.currentPage) || 1;
      const trigger = this.querySelector('[data-kr-load-more]');
      if (!(trigger instanceof HTMLAnchorElement)) break;

      await this.#appendPage(trigger.href, { restoring: true });
      if ((Number(grid.dataset.currentPage) || 1) <= before) break;
    }
  }

  /**
   * Appends the next page in place instead of replacing the grid.
   *
   * @param {string} href
   * @param {{ restoring?: boolean }} [options]
   */
  async #appendPage(href, { restoring = false } = {}) {
    const grid = this.querySelector('[data-kr-grid]');
    const more = this.querySelector('[data-kr-more]');
    if (!(grid instanceof HTMLElement)) return;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    if (more instanceof HTMLElement) more.setAttribute('aria-busy', 'true');

    try {
      const doc = await this.#fetchSection(new URL(href, window.location.origin), this.#fetchController.signal);
      if (!doc) return;

      const nextItems = doc.querySelectorAll('[data-kr-grid] > li');
      grid.append(...nextItems);

      const nextGrid = doc.querySelector('[data-kr-grid]');
      if (nextGrid instanceof HTMLElement && nextGrid.dataset.currentPage) {
        grid.dataset.currentPage = nextGrid.dataset.currentPage;
      }

      // The response carries the next "load more" anchor, or none when we've reached the end.
      const nextMore = doc.querySelector('[data-kr-more]');
      if (more instanceof HTMLElement) {
        if (nextMore) {
          more.replaceChildren(...nextMore.childNodes);
        } else {
          more.hidden = true;
          this.#scrollObserver?.disconnect();
        }
      }

      // The address stays on page 1; only the number of loaded pages is remembered.
      if (!restoring) {
        const pages = Number(grid.dataset.currentPage) || 1;
        history.replaceState({ krCollection: true, krPages: pages }, '', window.location.href);
      }
      this.#announce();
      if (!restoring) this.#setUpInfiniteScroll();
    } catch (error) {
      if (/** @type {Error} */ (error)?.name === 'AbortError') return;
      if (restoring) return;
      window.location.assign(href);
    } finally {
      if (more instanceof HTMLElement) more.setAttribute('aria-busy', 'false');
    }
  }

  #setUpInfiniteScroll() {
    this.#scrollObserver?.disconnect();
    if (this.dataset.pagination !== 'infinite') return;

    const more = this.querySelector('[data-kr-more]');
    const trigger = this.querySelector('[data-kr-load-more]');
    if (!(more instanceof HTMLElement) || !(trigger instanceof HTMLAnchorElement)) return;

    more.hidden = false;
    this.#scrollObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#scrollObserver?.disconnect();
            this.#appendPage(trigger.href);
          }
        }
      },
      { rootMargin: '400px 0px' }
    );

    this.#scrollObserver.observe(more);
  }

  /** @param {HTMLElement} button */
  #toggleFilterOverflow(button) {
    const panel = button.closest('.kr-filter__panel');
    if (!panel) return;

    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    for (const item of panel.querySelectorAll('[data-kr-filter-overflow]')) {
      /** @type {HTMLElement} */ (item).hidden = isExpanded;
    }

    button.setAttribute('aria-expanded', String(!isExpanded));
    button.textContent = isExpanded
      ? (button.dataset.labelMore ?? '')
      : (button.dataset.labelLess ?? '');
  }

  #restoreDensity() {
    if (!this.querySelector('[data-kr-density]')) return;

    let stored = null;
    try {
      stored = sessionStorage.getItem(DENSITY_KEY);
    } catch {
      // Private browsing modes can throw on storage access; the default density is fine.
    }

    this.#setDensity(stored ?? 'compact', { persist: false });
  }

  /**
   * @param {string} density
   * @param {{ persist?: boolean }} [options]
   */
  #setDensity(density, { persist = true } = {}) {
    this.classList.toggle('kr-collection--density-comfortable', density === 'comfortable');

    for (const button of this.querySelectorAll('[data-kr-density]')) {
      const el = /** @type {HTMLElement} */ (button);
      el.setAttribute('aria-pressed', String(el.dataset.krDensity === density));
    }

    if (!persist) return;
    try {
      sessionStorage.setItem(DENSITY_KEY, density);
    } catch {
      // Non-fatal: the choice just won't survive the next page load.
    }
  }
}

/**
 * <kr-filter-drawer> — the filter panel.
 *
 * In `sidebar` mode on desktop it stays a plain block in the flow; everywhere else it becomes
 * an overlay. The `data-overlay` attribute is what switches the CSS, and it is only ever set
 * from here, so a visitor without JavaScript always gets the inline, always-open version.
 */
class KrFilterDrawer extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {MediaQueryList | undefined} */
  #desktop;
  /** @type {HTMLElement | null} */
  #lastFocused = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#desktop = window.matchMedia(DESKTOP_QUERY);
    this.#desktop.addEventListener('change', this.#syncMode, { signal });
    this.#syncMode();

    document.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.addEventListener('click', this.#handleClick, { signal });

    const section = this.closest('kr-collection');
    section?.addEventListener('click', this.#handleTriggerClick, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#unlockScroll();
  }

  get #isOverlay() {
    return this.hasAttribute('data-overlay');
  }

  #syncMode = () => {
    const inline = this.dataset.mode === 'sidebar' && this.#desktop?.matches;

    if (inline) {
      this.close();
      this.removeAttribute('data-overlay');
    } else {
      this.setAttribute('data-overlay', '');
    }
  };

  /** @param {MouseEvent} event */
  #handleTriggerClick = (event) => {
    const trigger = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-kr-filter-open]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    this.#lastFocused = trigger;
    this.open();
  };

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // A click on the element itself (rather than the panel) is a click on the scrim.
    if (target === this || target.closest?.('[data-kr-filter-close]')) {
      event.preventDefault();
      this.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (!this.hasAttribute('data-open')) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key === 'Tab') this.#trapFocus(event);
  };

  /** @param {KeyboardEvent} event */
  #trapFocus(event) {
    const focusable = [
      ...this.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
      ),
    ].filter((el) => /** @type {HTMLElement} */ (el).offsetParent !== null);

    if (focusable.length === 0) return;

    const first = /** @type {HTMLElement} */ (focusable[0]);
    const last = /** @type {HTMLElement} */ (focusable[focusable.length - 1]);

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  open() {
    if (!this.#isOverlay) return;

    this.setAttribute('data-open', '');
    this.#lockScroll();
    this.#setTriggerExpanded(true);

    const close = this.querySelector('[data-kr-filter-close]');
    if (close instanceof HTMLElement) close.focus();
  }

  close() {
    if (!this.hasAttribute('data-open')) return;

    this.removeAttribute('data-open');
    this.#unlockScroll();
    this.#setTriggerExpanded(false);
    this.#lastFocused?.focus();
  }

  /** @param {boolean} expanded */
  #setTriggerExpanded(expanded) {
    const trigger = this.closest('kr-collection')?.querySelector('[data-kr-filter-open]');
    trigger?.setAttribute('aria-expanded', String(expanded));
  }

  #lockScroll() {
    document.documentElement.style.setProperty('overflow', 'hidden');
  }

  #unlockScroll() {
    document.documentElement.style.removeProperty('overflow');
  }
}

if (!customElements.get('kr-filter-drawer')) {
  customElements.define('kr-filter-drawer', KrFilterDrawer);
}

if (!customElements.get('kr-collection')) {
  customElements.define('kr-collection', KrCollection);
}
